import React, { useState, useEffect } from "react";
import { MultiSelect, Select } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import useStyles from "./style";
import { DateInput } from "@mantine/dates";
import moment from "moment";
import { FiCalendar } from "react-icons/fi";

const days = [
  { label: "Sun", full: "Sunday" },
  { label: "Mon", full: "Monday" },
  { label: "Tues", full: "Tuesday" },
  { label: "Wed", full: "Wednesday" },
  { label: "Thur", full: "Thursday" },
  { label: "Fri", full: "Friday" },
  { label: "Sat", full: "Saturday" },
];

const ScheduleMenu = ({ form, isPreview, data, scannedBadge, showDetails }) => {
  const { classes } = useStyles();
  const [selectedDays, setSelectedDays] = useState(
    form.getInputProps("repeatOn")?.value || [moment().format("dddd")],
  );

  useEffect(() => {
    const savedRepeatOn = form.getInputProps("repeatOn")?.value;
    if (Array.isArray(savedRepeatOn) && savedRepeatOn.length > 0) {
      setSelectedDays(savedRepeatOn);
    }
  }, [form.getInputProps("repeatOn")?.value]);

  const toggleDay = (fullName) => {
    const index = selectedDays.indexOf(fullName);
    if (index === -1) {
      const updated = [...selectedDays, fullName];
      setSelectedDays(updated);
      form.setFieldValue("repeatOn", updated);
    } else {
      if (selectedDays.length > 1) {
        const updated = selectedDays.filter((d) => d !== fullName);
        setSelectedDays(updated);
        form.setFieldValue("repeatOn", updated);
      }
    }
  };

  const scheduleType = form.getInputProps("scheduleType")?.value ?? "daily";
  const repeatEvery = form.getInputProps("repeatEvery")?.value ?? 1;
  const repeatType = form.getInputProps("repeatType")?.value ?? "week";
  const timeSlots = form.getInputProps("timeSlots")?.value ?? [];
  const medicationDate = form.getInputProps("medicationDate")?.value;

  // Build summary text shown in the trigger button
  const summaryParts = [];
  if (scheduleType === "daily") {
    summaryParts.push("Daily");
  } else if (scheduleType === "custom") {
    const repeatTypeLabel =
      { day: "Daily", week: "Weekly", month: "Monthly" }[repeatType] ||
      repeatType;
    summaryParts.push(`${repeatTypeLabel}, every ${repeatEvery}`);
    if (repeatType === "week" && selectedDays.length > 0) {
      summaryParts.push(selectedDays.map((d) => d.slice(0, 3)).join(", "));
    }
  }
  if (medicationDate)
    summaryParts.push(moment(medicationDate).format("MMM D, YYYY"));
  if (timeSlots.length > 0) summaryParts.push(timeSlots.join(", "));
  const summaryText = summaryParts.join(" · ");

  return (
    <>
      {showDetails && (
        <div className={classes.scheduleDetailsBox}>
          {/* Custom fields: Frequency + Repeat */}
          {scheduleType === "custom" && (
            <div style={{ display: "flex", gap: "16px" }}>
              {/* Frequency */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select
                  data={[
                    { value: "day", label: "Daily" },
                    { value: "week", label: "Weekly" },
                    { value: "month", label: "Monthly" },
                  ]}
                  label="Frequency"
                  placeholder="Select Frequency"
                  rightSection={<IconChevronDown />}
                  disabled={isPreview}
                  {...form.getInputProps("repeatType")}
                  onChange={(value) => {
                    form.setFieldValue("repeatType", value);
                    if (value !== "week") {
                      form.setFieldValue("repeatOn", []);
                      setSelectedDays([]);
                    }
                  }}
                  className={classes.inputStyle}
                />
              </div>

              {/* Repeat counter */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "var(--app-text)",
                    marginBottom: "6px",
                  }}
                >
                  Repeat
                </label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "var(--app-surface)",
                    border: "1px solid var(--app-border)",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    boxSizing: "border-box",
                    width: "100%",
                    height: "42px",
                  }}
                >
                  <button
                    type="button"
                    disabled={isPreview}
                    onClick={() => {
                      const v = Number(repeatEvery) || 1;
                      form.setFieldValue("repeatEvery", Math.max(1, v - 1));
                    }}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      border: "1.5px solid var(--app-border)",
                      backgroundColor: "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      flexShrink: 0,
                      padding: 0,
                      lineHeight: 1,
                      cursor: isPreview ? "not-allowed" : "pointer",
                      opacity: isPreview ? 0.4 : 1,
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={repeatEvery}
                    disabled={isPreview}
                    onChange={(e) =>
                      form.setFieldValue("repeatEvery", Number(e.target.value))
                    }
                    style={{
                      flex: 1,
                      border: "none",
                      backgroundColor: "transparent",
                      textAlign: "center",
                      outline: "none",
                      fontSize: "14px",
                      color: isPreview ? "var(--app-text-muted)" : "var(--app-text)",
                      minWidth: 0,
                    }}
                  />
                  <button
                    type="button"
                    disabled={isPreview}
                    onClick={() => {
                      const v = Number(repeatEvery) || 1;
                      form.setFieldValue("repeatEvery", v + 1);
                    }}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      border: "1.5px solid var(--app-border)",
                      backgroundColor: "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      flexShrink: 0,
                      padding: 0,
                      lineHeight: 1,
                      cursor: isPreview ? "not-allowed" : "pointer",
                      opacity: isPreview ? 0.4 : 1,
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Repeat on (custom + weekly only) */}
          {scheduleType === "custom" && repeatType === "week" && (
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "var(--app-text)",
                  marginBottom: "8px",
                }}
              >
                Repeat on
              </label>
              <div style={{ display: "flex", gap: "26px" }}>
                {days.map(({ label, full }) => {
                  const active = selectedDays.includes(full);
                  return (
                    <button
                      key={full}
                      type="button"
                      disabled={isPreview}
                      onClick={() => toggleDay(full)}
                      style={{
                        flex: 1,
                        padding: "10px 4px",
                        borderRadius: "8px",
                        border: active
                          ? "1.5px solid var(--app-primary)"
                          : "1.5px solid var(--app-border)",
                        backgroundColor: active ? "var(--app-primary)" : "var(--app-surface)",
                        color: active ? "var(--app-on-primary)" : "var(--app-text)",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: isPreview ? "not-allowed" : "pointer",
                        opacity: isPreview ? 0.6 : 1,
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        minWidth: 0,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row 3: Start Date + Administering Times */}
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <DateInput
                placeholder="mm/dd/yyyy"
                label={<span>Medication Start Date{scannedBadge?.("medicationDate")}</span>}
                withAsterisk
                icon={<FiCalendar />}
                {...form.getInputProps("medicationDate")}
                disabled={isPreview}
                className={classes.inputStyle}
              />
            </div>


                      <div style={{ flex: 1, minWidth: 0 }}>
                      <DateInput
                        id="medication-end-date"
                        minDate={form.values.medicationDate}
                        maxDate={form.values.expirationDate || undefined}
                        placeholder={"Pick a date"}
                        label="Medication End Date"
                        disabled={isPreview}
                        {...form.getInputProps("newMedicationEndDate")}
                        clearable
                        className={classes.inputStyle}
                        icon={<FiCalendar />}
                      />
                    </div>
                      
         
          </div>
             
            <div style={{ flex: 1, minWidth: 0 }}>
              <MultiSelect
                id="administering-time"
                disabled={isPreview}
                withAsterisk
                label={<span>Administering Time(s){scannedBadge?.("timeSlots")}</span>}
                data={[
                  "12:00 AM",
                  "12:30 AM",
                  "01:00 AM",
                  "01:30 AM",
                  "02:00 AM",
                  "02:30 AM",
                  "03:00 AM",
                  "03:30 AM",
                  "04:00 AM",
                  "04:30 AM",
                  "05:00 AM",
                  "05:30 AM",
                  "06:00 AM",
                  "06:30 AM",
                  "07:00 AM",
                  "07:30 AM",
                  "08:00 AM",
                  "08:30 AM",
                  "09:00 AM",
                  "09:30 AM",
                  "10:00 AM",
                  "10:30 AM",
                  "11:00 AM",
                  "11:30 AM",
                  "12:00 PM",
                  "12:30 PM",
                  "01:00 PM",
                  "01:30 PM",
                  "02:00 PM",
                  "02:30 PM",
                  "03:00 PM",
                  "03:30 PM",
                  "04:00 PM",
                  "04:30 PM",
                  "05:00 PM",
                  "05:30 PM",
                  "06:00 PM",
                  "06:30 PM",
                  "07:00 PM",
                  "07:30 PM",
                  "08:00 PM",
                  "08:30 PM",
                  "09:00 PM",
                  "09:30 PM",
                  "10:00 PM",
                  "10:30 PM",
                  "11:00 PM",
                  "11:30 PM",
                ]}
                rightSection={<IconChevronDown />}
                placeholder="Select Time(s)"
                searchable
                {...form.getInputProps("timeSlots")}
                className={classes.inputStyle}
              />
            </div>
        </div>
      )}
    </>
  );
};

export default ScheduleMenu;
